import { ApiResource } from '@/core/models/api-resource.interface';
import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { Module } from 'vuex';
import cluster, { ClusterState } from './clusters/cluster.store';
import employer, { EmployerState } from './employers/employer.store';
import icon, { IconState } from './icon/icon.store';
import internalJobFunction, { InternalJobFunctionState } from './internal-job-functions/internal-job-function.store';
import logo, { LogoState } from './logo/logo.store';
import module, { ModuleState } from './modules/module.store';
import role, { RoleState } from './roles/role.store';
import team, { TeamState } from './teams/team.store';
import { Tenant } from './tenant.interface';
import { TenantService } from './tenant.service';
import user, { UserState } from './user/user.store';

const service = new TenantService();

export interface TenantState {
   tenantsOverview?: ApiResource<Array<Tenant>>;
   tenant?: Tenant;
   loadingTenant: boolean;

   // sub modules
   cluster?: ClusterState;
   internalJobFunction?: InternalJobFunctionState;
   module?: ModuleState;
   role?: RoleState;
   team?: TeamState;
   user?: UserState;
   logo?: LogoState;
   icon?: IconState;
   employer?: EmployerState;
}

const TenantStore: Module<TenantState, StoreState> = {
   namespaced: true,
   state: {
      loadingTenant: false,
   },
   getters: {},
   mutations: {
      SET_TENANTS_OVERVIEW(state, tenantsOverview?: ApiResource<Array<Tenant>>) {
         state.tenantsOverview = tenantsOverview;
      },
      SET_TENANT(state, tenant: Tenant) {
         state.tenant = tenant;
      },
      SET_TENANT_LOGO(state, tenantLogo: string) {
         if (!state.tenant) return;
         state.tenant.logo = tenantLogo;
      },
      SET_TENANT_ICON(state, tenantIcon: string) {
         if (!state.tenant) return;
         state.tenant.icon = tenantIcon;
      },
      SET_LOADING_TENANT(state, loading: boolean) {
         state.loadingTenant = loading;
      },
      REMOVE_TENANT(state, tenantId: number) {
         const index = (state.tenantsOverview?.data || []).findIndex((t) => t.id === tenantId);
         if (index > -1) state.tenantsOverview?.data.splice(index, 1);
      },
      ADD_TENANT_MODULE(state, module) {
         if (!state.tenant?.modules?.find((m) => m.id === module.id)) {
            state.tenant?.modules?.push(module);
         }
      },
      REMOVE_TENANT_MODULE(state, moduleId) {
         const moduleIndex = (state.tenant?.modules || []).findIndex((module) => module.id === moduleId);
         if (moduleIndex > -1) state.tenant?.modules?.splice(moduleIndex, 1);
      },
   },
   actions: {
      async searchTenants(context, query?: LocationQuery) {
         const response = await service.getTenants({ ...query });
         return response.data;
      },
      async getTenants(context, query?: LocationQuery) {
         const tenants = await service.getTenants(query);
         context.commit('SET_TENANTS_OVERVIEW', tenants);
         return tenants;
      },
      async getTenant(context, payload: { tenantId: number }): Promise<Tenant> {
         context.commit('SET_LOADING_TENANT', true);

         return await service
            .getTenant(payload.tenantId)
            .then((tenant) => {
               context.commit('SET_TENANT', tenant);
               return tenant;
            })
            .finally(() => context.commit('SET_LOADING_TENANT', false));
      },
      async createTenant(context, tenant: Tenant) {
         let response = await service.postTenant({ ...tenant, logo: undefined });

         console.log(tenant);

         if (tenant.logo && typeof tenant.logo === 'object') {
            response = await context.dispatch('uploadType', { tenantId: response.id, type: 'logo', file: tenant.logo });
         }
         if (tenant.icon && typeof tenant.icon === 'object') {
            response = await context.dispatch('uploadType', { tenantId: response.id, type: 'icon', file: tenant.icon });
         }

         context.commit('ADD_TENANT', response);
         return response;
      },
      async changeTenant(context, payload: { tenant: Tenant; tenantId: number; query?: LocationQuery }) {
         let response = await service.putTenant(payload.tenantId, { ...payload.tenant, logo: undefined }, payload.query);
         if (payload.tenant.logo && typeof payload.tenant.logo === 'object') {
            response = await context.dispatch('uploadType', { tenantId: response.id, type: 'logo', file: payload.tenant.logo });
         }
         if (payload.tenant.icon && typeof payload.tenant.icon === 'object') {
            response = await context.dispatch('uploadType', { tenantId: response.id, type: 'icon', file: payload.tenant.icon });
         }

         context.commit('SET_TENANT', response);
         return response;
      },
      async removeTenant(context, tenantId: number) {
         const resposne = await service.deleteTenant(tenantId);
         context.commit('REMOVE_TENANT', tenantId);
         return resposne;
      },

      async uploadType(context, payload: { tenantId: number; type: string; file: any }) {
         try {
            const path = `tenant/${payload.type}/upload${payload.type.charAt(0).toUpperCase() + payload.type.slice(1)}`;
            const body = { tenantId: payload.tenantId, [payload.type]: payload.file };
            const options = { root: true };

            return await context.dispatch(path, body, options);
         } catch {
            console.info(`Could not upload ${payload.type}`);
         }
      },
   },

   modules: {
      cluster,
      internalJobFunction,
      module,
      role,
      team,
      user,
      logo,
      icon,
      employer,
   },
};

export default TenantStore;
